/**
 * Surface Layout Component
 * Phase 8: Surface Host Components - Layout Management with Peek Surfaces
 * 
 * Layout component that:
 * - Defines layout regions (sidebar, main, peek)
 * - Responsive breakpoints for mobile, tablet, desktop
 * - Grid/flex configuration for region arrangement
 * - Integrated peek surface support
 * - Props: sidebar, main, peek content
 */

import React, { useMemo } from 'react';
import { Box, Group, Stack, Container } from '@mantine/core';
import type { CSSProperties } from 'react';
import { usePeek } from './PeekManager';

/**
 * Breakpoint definitions (pixels)
 */
const BREAKPOINTS = {
  mobile: 480,
  tablet: 768,
  desktop: 1024,
  wide: 1400,
} as const;

/**
 * Layout configuration for different screen sizes
 */
interface LayoutConfig {
  sidebarWidth: number | string;
  peekWidth: number | string;
  mainFlexGrow: number;
  sidebarVisible: boolean;
  peekVisible: boolean;
  direction: 'column' | 'row';
  spacing: number | string;
}

type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Layout configurations for each breakpoint
 */
const LAYOUT_CONFIGS: Record<BreakpointKey, LayoutConfig> = {
  mobile: {
    sidebarWidth: '100%',
    peekWidth: '100%',
    mainFlexGrow: 1,
    sidebarVisible: true,
    peekVisible: false,
    direction: 'column',
    spacing: 0,
  },
  tablet: {
    sidebarWidth: '250px',
    peekWidth: '100%',
    mainFlexGrow: 1,
    sidebarVisible: true,
    peekVisible: false,
    direction: 'row',
    spacing: 0,
  },
  desktop: {
    sidebarWidth: '250px',
    peekWidth: '300px',
    mainFlexGrow: 1,
    sidebarVisible: true,
    peekVisible: true,
    direction: 'row',
    spacing: 0,
  },
  wide: {
    sidebarWidth: '280px',
    peekWidth: '320px',
    mainFlexGrow: 1,
    sidebarVisible: true,
    peekVisible: true,
    direction: 'row',
    spacing: 0,
  },
};

interface SurfaceLayoutProps {
  /** Sidebar content */
  sidebar?: React.ReactNode;
  /** Main content area */
  main: React.ReactNode;
  /** Peek panel content (right panel) */
  peek?: React.ReactNode;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: CSSProperties;
  /** Enable responsive behavior (default: true) */
  responsive?: boolean;
  /** Force specific layout (overrides responsive) */
  forceLayout?: 'mobile' | 'tablet' | 'desktop' | 'wide';
  /** Custom spacing between regions */
  spacing?: number | string;
  /** Show debug borders */
  debugMode?: boolean;
}

/**
 * Surface Layout Component
 * 
 * Manages the layout of the surface application with sidebar, main content,
 * and peek panel. Automatically adjusts to screen size and supports custom configurations.
 */
const SurfaceLayout: React.FC<SurfaceLayoutProps> = ({
  sidebar,
  main,
  peek,
  className,
  style,
  responsive = true,
  forceLayout,
  spacing = 0,
  debugMode = false,
}) => {
  /**
   * Determine current layout based on screen size
   */
  const currentLayout = useMemo<BreakpointKey>(() => {
    // If forced layout is specified, use it
    if (forceLayout) {
      return forceLayout;
    }

    // If responsive is disabled, use desktop layout
    if (!responsive) {
      return 'desktop';
    }

    // Determine layout based on window width
    if (typeof window === 'undefined') {
      return 'desktop';
    }

    const width = window.innerWidth;

    if (width < BREAKPOINTS.tablet) {
      return 'mobile';
    } else if (width < BREAKPOINTS.desktop) {
      return 'tablet';
    } else if (width < BREAKPOINTS.wide) {
      return 'desktop';
    }

    return 'wide';
  }, [forceLayout, responsive]);

  /**
   * Get layout configuration for current breakpoint
   */
  const layoutConfig = useMemo(() => {
    return LAYOUT_CONFIGS[currentLayout];
  }, [currentLayout]);

  /**
   * Handle window resize to update layout
   */
  React.useEffect(() => {
    if (!responsive) return;

    const handleResize = () => {
      // Trigger re-render by updating state
      // This will cause layout recalculation
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [responsive]);

  /**
   * Render sidebar region
   */
  const renderSidebar = () => {
    if (!sidebar || !layoutConfig.sidebarVisible) {
      return null;
    }

    return (
      <Box
        component="nav"
        w={layoutConfig.sidebarWidth}
        h="100%"
        style={{
          flexShrink: 0,
          borderRight: '1px solid var(--mantine-color-gray-2)',
          overflowY: 'auto',
          overflowX: 'hidden',
          ...(debugMode && {
            outline: '2px solid red',
          }),
        }}
      >
        {sidebar}
      </Box>
    );
  };

  /**
   * Render main content region
   */
  const renderMain = () => {
    return (
      <Box
        component="main"
        flex={layoutConfig.mainFlexGrow}
        h="100%"
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...(debugMode && {
            outline: '2px solid green',
          }),
        }}
      >
        {main}
      </Box>
    );
  };

  /**
   * Render peek panel region
   */
  const renderPeek = () => {
    if (!peek || !layoutConfig.peekVisible) {
      return null;
    }

    return (
      <Box
        component="aside"
        w={layoutConfig.peekWidth}
        h="100%"
        style={{
          flexShrink: 0,
          borderLeft: '1px solid var(--mantine-color-gray-2)',
          overflowY: 'auto',
          overflowX: 'hidden',
          ...(debugMode && {
            outline: '2px solid blue',
          }),
        }}
      >
        {peek}
      </Box>
    );
  };

  return (
    <Box
      className={className}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: layoutConfig.direction as any,
        gap: layoutConfig.spacing,
        position: 'relative',
        ...style,
      }}
    >
      {/* Sidebar */}
      {renderSidebar()}

      {/* Main content */}
      {renderMain()}

      {/* Peek panel */}
      {renderPeek()}

      {/* Debug info */}
      {debugMode && (
        <Box
          style={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            fontSize: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            zIndex: 9999,
          }}
        >
          <div>Layout: {currentLayout}</div>
          <div>Width: {typeof window !== 'undefined' ? window.innerWidth : 'N/A'}px</div>
          <div>Sidebar: {layoutConfig.sidebarVisible ? 'visible' : 'hidden'}</div>
          <div>Peek: {layoutConfig.peekVisible ? 'visible' : 'hidden'}</div>
        </Box>
      )}
    </Box>
  );
};

export default SurfaceLayout;
export type { SurfaceLayoutProps };
export { BREAKPOINTS, LAYOUT_CONFIGS, type LayoutConfig };
